// components/navbar/index.jsx - Professional Yellow Theme
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Typography,
  Divider,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
  Stack,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { getSecureLocalItem } from "utils/secureSessionStorage";
import { logoutAction } from "store/features/auth/authActions";
import { toggleDarkmode } from "store/features/theme/themeSlice";
import avatar from "assets/img/avatars/avatar.png";

const Navbar = ({
  onToggleSidebar,
  sidebarOpen,
  sidebarCollapsed,
  isMobile,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get darkmode from Redux
  const darkmode = useSelector((state) => state.theme?.darkmode ?? false);

  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  const role = getSecureLocalItem("role") || "admin";

  // Dynamic user info based on role
  const getUserName = () => {
    if (role === "SUPER_ADMIN") return "Super Admin";
    if (role === "Company") return "Company Admin";
    return "Administrator";
  };

  const getDashboardPath = () => {
    if (role === "SUPER_ADMIN") return "/admin/dashboard";
    if (role === "Company") return "/company/dashboard";
    return "/admin/dashboard";
  };

  const getProfilePath = () => {
    if (role === "SUPER_ADMIN") return "/admin/profile";
    if (role === "Company") return "/company/profile";
    return "/admin/profile";
  };

  const getSettingsPath = () => {
    if (role === "SUPER_ADMIN") return "/admin/settings";
    if (role === "Company") return "/company/settings";
    return "/admin/settings";
  };

  const userName = getUserName();
  // const userEmail = `${role?.toLowerCase()}@ebspms.com`;

  // Mock notifications
  const notifications = [
    {
      id: 1,
      title: "Welcome!",
      description: "Welcome to EBS Office Management System",
      time: "Just now",
      read: false,
    },
  ];

  const unreadCount = notifications.filter((n) => !n.read).length;

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNotificationsOpen = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };

  const handleProfileOpen = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setProfileAnchorEl(null);
  };

  const handleLogout = useCallback(() => {
    dispatch(logoutAction());
    navigate("/auth/sign-in");
  }, [dispatch, navigate]);

  const notificationsOpen = Boolean(notificationsAnchorEl);
  const profileOpen = Boolean(profileAnchorEl);

  // Handle theme toggle
  const handleToggleTheme = () => {
    dispatch(toggleDarkmode());
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          zIndex: 40,
          backgroundColor:
            theme.palette.mode === "dark"
              ? alpha("#1e293b", 0.95)
              : alpha("#ffffff", 0.95),
          backdropFilter: "blur(24px)",
          borderBottom: `1px solid ${
            theme.palette.mode === "dark"
              ? "rgba(245,158,11,0.08)"
              : "rgba(245,158,11,0.08)"
          }`,
          boxShadow: scrolled ? `0 4px 20px ${alpha("#d97706", 0.08)}` : "none",
          transition: "all 0.3s",
        }}
      >
        <Toolbar
          sx={{
            minHeight: "64px !important",
            px: { xs: 2, lg: 3 },
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          {/* Left Section */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {/* Sidebar Toggle Button */}
            <motion.div whileTap={{ scale: 0.95 }}>
              <IconButton
                onClick={onToggleSidebar}
                size="small"
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "12px",
                  backgroundColor:
                    sidebarOpen && !sidebarCollapsed
                      ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                      : theme.palette.mode === "dark"
                      ? "#1e293b"
                      : "#f3f4f6",
                  color: theme.palette.mode === "dark" ? "#9ca3af" : "#4b5563",
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(245,158,11,0.2)"
                        : "#fffbeb",
                    color: "#f59e0b",
                  },
                }}
              >
                {sidebarOpen && !sidebarCollapsed ? (
                  <CloseIcon />
                ) : (
                  <MenuIcon />
                )}
              </IconButton>
            </motion.div>

            {/* Brand for Mobile */}
            {isMobile && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    color:
                      theme.palette.mode === "dark" ? "#ffffff" : "#1f2937",
                  }}
                >
                  EBS<span style={{ color: "#f59e0b" }}> OMS</span>
                </Typography>
              </Box>
            )}
          </Box>

          {/* Right Section */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Theme Toggle */}
            <IconButton
              onClick={handleToggleTheme}
              size="small"
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                backgroundColor:
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6",
                color: theme.palette.mode === "dark" ? "#9ca3af" : "#4b5563",
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(245,158,11,0.2)"
                      : "#fffbeb",
                  color: "#f59e0b",
                },
              }}
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={darkmode ? "dark" : "light"}
                  initial={{ rotate: -180, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 180, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {darkmode ? (
                    <LightModeIcon sx={{ color: "#f59e0b" }} />
                  ) : (
                    <DarkModeIcon />
                  )}
                </motion.div>
              </AnimatePresence>
            </IconButton>

            {/* Profile Dropdown */}
            <Box>
              <IconButton
                onClick={handleProfileOpen}
                size="small"
                sx={{
                  borderRadius: "12px",
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6",
                  p: 0.75,
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(245,158,11,0.2)"
                        : "#fffbeb",
                  },
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ px: 1 }}
                >
                  <Box sx={{ position: "relative" }}>
                    <Avatar
                      src={avatar}
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: -2,
                        right: -2,
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: "#10b981",
                        border: `2px solid ${
                          theme.palette.mode === "dark" ? "#1e293b" : "#ffffff"
                        }`,
                      }}
                    />
                  </Box>

                  {!isMobile && (
                    <>
                      <Box sx={{ textAlign: "left" }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: "semibold",
                            color:
                              theme.palette.mode === "dark"
                                ? "#ffffff"
                                : "#1f2937",
                          }}
                        >
                          {userName}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color:
                              theme.palette.mode === "dark"
                                ? "#9ca3af"
                                : "#6b7280",
                          }}
                        >
                          {role?.replace("_", " ")}
                        </Typography>
                      </Box>
                      <ChevronRightIcon
                        sx={{
                          fontSize: 16,
                          color: "#9ca3af",
                          transition: "transform 0.2s",
                          transform: profileOpen ? "rotate(90deg)" : "none",
                        }}
                      />
                    </>
                  )}
                </Stack>
              </IconButton>
              {/* Profile Menu - Beautifully Animated */}
              <Menu
                anchorEl={profileAnchorEl}
                open={profileOpen}
                onClose={handleProfileClose}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    width: 300,
                    borderRadius: "20px",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? alpha("#1e293b", 0.98)
                        : alpha("#ffffff", 0.98),
                    backdropFilter: "blur(12px)",
                    border: `1px solid ${alpha(
                      theme.palette.primary.main,
                      0.15,
                    )}`,
                    boxShadow: `0 20px 40px ${alpha("#000", 0.15)}`,
                    overflow: "hidden",
                  },
                }}
              >
                <AnimatePresence>
                  {profileOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -20, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -20, scale: 0.95 }}
                      transition={{
                        type: "spring",
                        damping: 20,
                        stiffness: 300,
                      }}
                    >
                      {/* Profile Header with Gradient Background */}
                      <Box
                        sx={{
                          p: 2.5,
                          background: `linear-gradient(135deg, ${alpha(
                            theme.palette.primary.main,
                            0.15,
                          )} 0%, ${alpha(
                            theme.palette.primary.dark,
                            0.08,
                          )} 100%)`,
                          borderBottom: `1px solid ${alpha(
                            theme.palette.divider,
                            0.1,
                          )}`,
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        {/* Animated Decorative Circles */}
                        <motion.div
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 0.5 }}
                          transition={{ delay: 0.1, duration: 0.5 }}
                          style={{
                            position: "absolute",
                            top: -30,
                            right: -30,
                            width: 100,
                            height: 100,
                            borderRadius: "50%",
                            background: `radial-gradient(circle, ${alpha(
                              theme.palette.primary.main,
                              0.2,
                            )} 0%, transparent 70%)`,
                          }}
                        />
                        <motion.div
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 0.3 }}
                          transition={{ delay: 0.2, duration: 0.5 }}
                          style={{
                            position: "absolute",
                            bottom: -20,
                            left: -20,
                            width: 80,
                            height: 80,
                            borderRadius: "50%",
                            background: `radial-gradient(circle, ${alpha(
                              theme.palette.primary.light,
                              0.15,
                            )} 0%, transparent 70%)`,
                          }}
                        />

                        <Stack direction="row" spacing={2} alignItems="center">
                          {/* Animated Avatar */}
                          <motion.div
                            initial={{ scale: 0, rotate: -180 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{
                              type: "spring",
                              stiffness: 400,
                              damping: 15,
                              delay: 0.1,
                            }}
                          >
                            <Box sx={{ position: "relative" }}>
                              <Avatar
                                src={avatar}
                                sx={{
                                  width: 56,
                                  height: 56,
                                  borderRadius: "18px",
                                  border: `2px solid ${theme.palette.primary.main}`,
                                  boxShadow: `0 8px 20px ${alpha(
                                    theme.palette.primary.main,
                                    0.25,
                                  )}`,
                                }}
                              />
                              {/* Online Status Indicator with Pulse Animation */}
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.3 }}
                                style={{
                                  position: "absolute",
                                  bottom: 2,
                                  right: 2,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 14,
                                    height: 14,
                                    borderRadius: "50%",
                                    backgroundColor: "#10b981",
                                    border: `2px solid ${theme.palette.background.paper}`,
                                    animation: "pulse 2s infinite",
                                    "@keyframes pulse": {
                                      "0%": {
                                        opacity: 1,
                                        transform: "scale(1)",
                                      },
                                      "50%": {
                                        opacity: 0.6,
                                        transform: "scale(1.1)",
                                      },
                                      "100%": {
                                        opacity: 1,
                                        transform: "scale(1)",
                                      },
                                    },
                                  }}
                                />
                              </motion.div>
                            </Box>
                          </motion.div>

                          {/* User Info with Staggered Animation */}
                          <motion.div
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.15, duration: 0.3 }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 700,
                                fontSize: "1rem",
                                background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${theme.palette.primary.main} 100%)`,
                                backgroundClip: "text",
                                WebkitBackgroundClip: "text",
                                color: "transparent",
                              }}
                            >
                              {userName}
                            </Typography>
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: 0.2 }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: alpha(
                                    theme.palette.text.secondary,
                                    0.8,
                                  ),
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: "50%",
                                    backgroundColor: "#10b981",
                                    display: "inline-block",
                                  }}
                                />
                                {/* {userEmail} */}
                              </Typography>
                            </motion.div>
                          </motion.div>
                        </Stack>
                      </Box>

                      {/* Menu Items with Staggered Animations */}
                      <Box sx={{ p: 1.5 }}>
                        {/* My Profile Item */}
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.2, duration: 0.3 }}
                          whileHover={{ x: 8 }}
                        >
                          <MenuItem
                            onClick={() => {
                              navigate(getProfilePath());
                              handleProfileClose();
                            }}
                            sx={{
                              borderRadius: "12px",
                              py: 1.2,
                              mb: 0.5,
                              transition: "all 0.2s ease",
                              "&:hover": {
                                background: alpha(
                                  theme.palette.primary.main,
                                  0.1,
                                ),
                                "& .MuiListItemIcon-root": {
                                  transform: "scale(1.1)",
                                },
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{ transition: "transform 0.2s ease" }}
                            >
                              <motion.div
                                whileHover={{ rotate: 360 }}
                                transition={{ duration: 0.4 }}
                              >
                                <PersonIcon
                                  sx={{
                                    color: theme.palette.primary.main,
                                    fontSize: 22,
                                  }}
                                />
                              </motion.div>
                            </ListItemIcon>
                            <ListItemText>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  color: theme.palette.text.primary,
                                }}
                              >
                                My Profile
                              </Typography>
                            </ListItemText>
                            <motion.div
                              initial={{ x: -10, opacity: 0 }}
                              animate={{ x: 0, opacity: 1 }}
                              transition={{ delay: 0.25 }}
                            >
                              {/* <Box
                                sx={{
                                  width: 24,
                                  height: 24,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: "8px",
                                  background: alpha(
                                    theme.palette.primary.main,
                                    0.1,
                                  ),
                                }}
                              ></Box> */}
                            </motion.div>
                          </MenuItem>
                        </motion.div>

                        {/* Settings Item */}
                        {/* <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.25, duration: 0.3 }}
                          whileHover={{ x: 8 }}
                        >
                          <MenuItem
                            onClick={() => {
                              navigate(getSettingsPath());
                              handleProfileClose();
                            }}
                            sx={{
                              borderRadius: "12px",
                              py: 1.2,
                              mb: 0.5,
                              transition: "all 0.2s ease",
                              "&:hover": {
                                background: alpha(
                                  theme.palette.primary.main,
                                  0.1,
                                ),
                                "& .MuiListItemIcon-root": {
                                  transform: "rotate(90deg)",
                                },
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{ transition: "transform 0.3s ease" }}
                            >
                              <motion.div
                                whileHover={{ rotate: 90 }}
                                transition={{ duration: 0.3 }}
                              >
                                <SettingsIcon
                                  sx={{
                                    color: theme.palette.primary.main,
                                    fontSize: 22,
                                  }}
                                />
                              </motion.div>
                            </ListItemIcon>
                            <ListItemText>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  color: theme.palette.text.primary,
                                }}
                              >
                                Settings
                              </Typography>
                            </ListItemText>
                            <motion.div
                              initial={{ x: -10, opacity: 0 }}
                              animate={{ x: 0, opacity: 1 }}
                              transition={{ delay: 0.3 }}
                            >
                              <Box
                                sx={{
                                  width: 24,
                                  height: 24,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: "8px",
                                  background: alpha(
                                    theme.palette.primary.main,
                                    0.1,
                                  ),
                                }}
                              ></Box>
                            </motion.div>
                          </MenuItem>
                        </motion.div> */}

                        {/* Divider with Animation */}
                        <motion.div
                          initial={{ scaleX: 0 }}
                          animate={{ scaleX: 1 }}
                          transition={{ delay: 0.3, duration: 0.4 }}
                        >
                          <Divider
                            sx={{
                              my: 1.5,
                              borderColor: alpha(theme.palette.divider, 0.1),
                            }}
                          />
                        </motion.div>

                        {/* Logout Item */}
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.35, duration: 0.3 }}
                          whileHover={{ x: 8 }}
                        >
                          <MenuItem
                            onClick={handleLogout}
                            sx={{
                              borderRadius: "12px",
                              py: 1.2,
                              transition: "all 0.2s ease",
                              "&:hover": {
                                background: alpha("#ef4444", 0.1),
                                "& .MuiListItemIcon-root": {
                                  transform: "scale(1.1)",
                                },
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{ transition: "transform 0.2s ease" }}
                            >
                              <motion.div
                                whileHover={{ x: 5 }}
                                transition={{ duration: 0.2 }}
                              >
                                <LogoutIcon
                                  sx={{ color: "#ef4444", fontSize: 22 }}
                                />
                              </motion.div>
                            </ListItemIcon>
                            <ListItemText>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: "#ef4444",
                                }}
                              >
                                Logout
                              </Typography>
                            </ListItemText>
                            <motion.div
                              initial={{ x: -10, opacity: 0 }}
                              animate={{ x: 0, opacity: 1 }}
                              transition={{ delay: 0.4 }}
                            ></motion.div>
                          </MenuItem>
                        </motion.div>
                      </Box>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Menu>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
    </>
  );
};

export default Navbar;
